import { useState } from 'react'

// @mui
import {
  Box,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Switch,
  Typography
} from '@mui/material'
import { alpha, styled } from '@mui/material/styles'

import useAuth from '../../../hooks/useAuth'

import { Iconify } from 'src/components/base'
import Avatar from 'src/components/base/Avatar'
import { useSettingsContext } from 'src/contexts/SettingsContext'
import createAvatar from 'src/utils/createAvatar'

const AccountButton = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  marginTop: theme.spacing(1),
  padding: theme.spacing(0.75),
  borderRadius: 14,
  border: `1px solid ${theme.palette.grey[500_24]}`,
  transition: theme.transitions.create(
    ['background-color', 'border-color', 'box-shadow'],
    { duration: theme.transitions.duration.shorter }
  ),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.24)}`
  }
}))

const ROLE_COLOR_MAP: Record<string, 'error' | 'warning' | 'info'> = {
  ADMIN: 'error',
  MANAGER: 'warning',
  USER: 'info'
}

export default function NavbarAccount() {
  const { user, logout, logoutFromAllDevices } = useAuth()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { themeMode, onToggleMode } = useSettingsContext()

  const displayName = user?.name || ''
  const role = user?.role || ''
  const open = Boolean(anchorEl)

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    handleClose()
    logout(user?.id)
  }

  const handleLogoutAllDevices = () => {
    handleClose()
    logoutFromAllDevices()
  }

  return (
    <>
      <AccountButton onClick={handleOpen}>
        <Avatar
          alt={displayName}
          color={createAvatar(displayName).color}
          sx={{ width: 38, height: 38, fontSize: 15 }}
        >
          {createAvatar(displayName).name}
        </Avatar>
      </AccountButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 240,
            mt: 1,
            overflow: 'visible',
            boxShadow:
              '0 0 2px 0 rgba(145,158,171,0.2), 0 12px 24px -4px rgba(145,158,171,0.12)',
            border: (theme) =>
              `1px solid ${alpha(theme.palette.grey[500], 0.12)}`
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1">{displayName}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.userName || ''}
          </Typography>
          {role && (
            <Chip
              label={role}
              size="small"
              color={ROLE_COLOR_MAP[role] || 'default'}
              sx={{
                mt: 0.75,
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 700
              }}
            />
          )}
        </Box>

        <Divider />

        <Box sx={{ p: 0.5 }}>
          <MenuItem
            disableRipple
            sx={{
              borderRadius: 1,
              py: 0.75,
              mb: 0.5
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Iconify
                icon={
                  themeMode === 'dark' ? 'eva:moon-fill' : 'eva:sun-outline'
                }
                width={20}
                height={20}
              />
            </ListItemIcon>
            <ListItemText
              primary="Dark mode"
              primaryTypographyProps={{ variant: 'body2' }}
            />
            <Switch
              edge="end"
              size="small"
              checked={themeMode === 'dark'}
              onChange={onToggleMode}
            />
          </MenuItem>

          <MenuItem onClick={handleLogout} sx={{ borderRadius: 1, py: 1 }}>
            <ListItemIcon>
              <Iconify icon="eva:log-out-outline" width={20} height={20} />
            </ListItemIcon>
            <ListItemText
              primary="Log out"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>

          <MenuItem
            onClick={handleLogoutAllDevices}
            sx={{ borderRadius: 1, py: 1, color: 'error.main' }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <Iconify icon="eva:monitor-outline" width={20} height={20} />
            </ListItemIcon>
            <ListItemText
              primary="Log out all devices"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        </Box>
      </Popover>
    </>
  )
}
